import { Box, IconButton } from '@chakra-ui/react';
import { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { MdClose } from 'react-icons/md';

import { FilterComponentProps, TableFilter } from '../types';
import { AutocompleteFilter } from './filters/autocomplete-filter';
import { BooleanFilter } from './filters/boolean-filter';
import { DateFilter } from './filters/date-filter';
import { DateRangeFilter } from './filters/date-range-filter';
import { MultiSelectFilter } from './filters/multi-select-filter';
import { SelectFilter } from './filters/select-filter';
import { TextFilter } from './filters/text-filter';
import { OutletFilter } from './filters/outlet-filter';
import { AutocompletePaginatedFilter } from './filters/autocomplete-paginated-filter';

export function FilterItem<I extends EntityBase = EntityBase>(props: FilterComponentProps<I>) {
  const {
    filter,
    onChange,
    onClear,
  } = props;

  const [scope, setScope] = useState<'include' | 'exclude'>('include');

  const onScopeChange = useCallback((equal: boolean) => {
    setScope(equal ? 'include' : 'exclude');
    if (filter) {
      onChange({ ...filter, scope: equal ? 'include' : 'exclude' });
    }
  }, [filter, onChange]);

  const onValueChange = useCallback((value: TableFilter<I>) => {
    onChange({ ...value, scope });
  }, [scope, onChange]);

  const ItemComponent = useMemo(() => {
    switch (filter.type) {
      case 'text': return <TextFilter<I> filter={filter} onChange={onValueChange} />;
      case 'select': return <SelectFilter<I> filter={filter} onChange={onValueChange} />;
      case 'multi-select': return <MultiSelectFilter<I> filter={filter} onChange={onValueChange} />;
      case 'autocomplete': return <AutocompleteFilter<I> filter={filter} onChange={onValueChange} />;
      case 'date': return <DateFilter<I> filter={filter} onChange={onValueChange} />;
      case 'date-range': return <DateRangeFilter<I> filter={filter} onChange={onValueChange} />;
      case 'boolean': return <BooleanFilter<I> filter={filter} onChange={onValueChange} />;
      case 'outlet': return <OutletFilter<I> filter={filter} onChange={onValueChange} />;
      case 'autocomplete-paginated': return <AutocompletePaginatedFilter<I> filter={filter} onChange={onValueChange} />;
    }
  }, [filter, onValueChange]);

  const onClearItem = useCallback(() => {
    onClear(filter.name);
  }, [filter, onClear]);

  return <FilterWrapper 
    isEquals={scope === 'include'}
    onChange={onScopeChange}
    onClear={onClearItem}
  >
    {ItemComponent}
  </FilterWrapper>;
}

export const FilterWrapper = ({ children, isEquals, onChange, onClear }: {
  isEquals: boolean;
  onChange(value: boolean): void;
  onClear(): void;
} & PropsWithChildren) => {
  
  return <Box display='flex' alignItems='center' gap={4}>
    {children}
    {/* <Box display='flex' alignItems='center' gap={1}>
      <IconButton icon={<FaEquals />} aria-label="equal" isActive={isEquals} onClick={() => onChange(true)} />
      <IconButton icon={<FaNotEqual />} aria-label="equal" isActive={!isEquals} onClick={() => onChange(false)} />
    </Box> */}
    <Box display='flex' alignItems='center'>
      <IconButton icon={<MdClose />} aria-label="equal" variant='ghost' onClick={onClear} />
    </Box>
  </Box>;
};